// Wave 16 — Paywall upsell modal.
//
// Opened by:
//   1. Any locked-endpoint 402 (SessionDetail, Analytics, AIClientVision)
//   2. Free-tier weekly cap reached mid-session (AIClientVision)
//   3. Explicit "Upgrade" click on the UsageDashboardCard
//
// Never renders on rapha.aero.chat (subdomain gate at the caller) or for
// Rapha-role users (their existing bypass in useSubscription.hasFullAccess).

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colors } from '../theme';
import { useSubscription, formatOverageRate } from '../services/useSubscription';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
// Wave 16 — checkout is gated behind the Stripe integration going live.
// While false, the modal shows a "Pro launching soon" state instead of
// upgrade buttons so users don't get silently promoted via stubActivate.
const STRIPE_ENABLED = process.env.REACT_APP_STRIPE_ENABLED === 'true';

export type PaywallReason =
  | 'weekly_cap'
  | 'locked_feature'
  | 'upgrade_click';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Which trigger opened this — tweaks the headline copy. */
  reason?: PaywallReason;
  /** Optional feature name for tighter locked-feature copy
   *  ("AI reports are Pro-only"). */
  featureName?: string;
}

// Wave 20.68 — the overage rate comes from the usage snapshot. It
// used to be typed here as $0.20, which meant changing the price in
// Stripe quietly left the paywall advertising the old one.
function proBullets(rate: string | null): string[] {
  return [
    'Unlimited aero-session time',
    rate ? `30 min/day included, ${rate} after` : '30 min/day included',
    'Unlimited saved positions in your Wake',
    'Full 8-day weather forecast',
    'Full analytics dashboards + AI reports',
  ];
}

export default function PaywallModal(props: Props) {
  const { open, onClose, reason, featureName } = props;
  const [busy, setBusy] = useState<string | null>(null);
  const { usage } = useSubscription();
  const overageRate = formatOverageRate(usage?.overageCentsPerMin);
  const PRO_BULLETS = proBullets(overageRate);

  const headline = !STRIPE_ENABLED
    ? 'Pro is launching soon'
    : reason === 'weekly_cap'
    ? 'Free weekly limit reached'
    : reason === 'locked_feature' && featureName
    ? `${featureName} is a Pro feature`
    : 'Unlock the full experience';

  const subhead = !STRIPE_ENABLED
    ? 'Pro tier is coming shortly — we\'re finishing the billing integration. Everyone is on Free while we get set up. Rapha members keep full access.'
    : reason === 'weekly_cap'
    ? `You have used your 4 min of free aero-session time this week. Pro riders get unlimited aero-session time (30 min/day included${
        overageRate ? `, then ${overageRate}` : ''
      }).`
    : 'Pro unlocks the dashboards, AI reports, and full analytics. Cancel any time.';

  const checkout = async (planId: string) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      window.location.href = '/login';
      return;
    }
    setBusy(planId);
    try {
      const r = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planId }),
      });
      const body = await r.json();
      if (body?.url) {
        if (body.url.startsWith('http')) {
          window.location.href = body.url;
        } else {
          window.location.href = body.url;
        }
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle sx={{ pt: 3, pb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {headline}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted, mt: 1 }}>
          {subhead}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            p: 2.5,
            mt: 1,
          }}
        >
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="h4" fontWeight={800}>
              $19.99
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              / month
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {PRO_BULLETS.map((b) => (
              <Stack key={b} direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon
                  sx={{ color: colors.brand, fontSize: 18 }}
                />
                <Typography variant="body2">{b}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        {!STRIPE_ENABLED ? (
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ backgroundColor: colors.brand }}
          >
            Got it
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: colors.textMuted }}>
              Not now
            </Button>
            <Button
              variant="contained"
              disabled={busy != null}
              onClick={() => checkout('pro_monthly')}
              sx={{ backgroundColor: colors.brand }}
            >
              {busy === 'pro_monthly' ? 'Loading…' : 'Try Pro — $19.99/mo'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
